import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "react-i18next";
import Container from "../components/Container";
import usePortfolioStore from "../store/store";
import DriveImage from "../components/DriveImage";
import Loading from "../components/loading";

export default function Blogs() {
  const { colors } = useTheme();
  const { t, i18n } = useTranslation("common");
  const [loading, setLoading] = useState(true);
  const blogs = usePortfolioStore((s) => s.blogs || []);
  const fetchBlogsRemote = usePortfolioStore((s) => s.fetchBlogsRemote);
  const dateLocaleMap = {
    en: "en-US",
    cn: "zh-CN",
    th: "th-TH",
    my: "my-MM",
  };
  const activeDateLocale =
    dateLocaleMap[i18n.resolvedLanguage] || dateLocaleMap.en;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchBlogsRemote();
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchBlogsRemote]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Container>
      <div className="py-8">
        {/* Header */}
        <div className="mb-4 lg:mb-8">
          <h2
            className={`text-lg md:text-2xl lg:text-4xl font-bold ${colors.text.primary}`}
          >
            {t("blogs.title")}
          </h2>
          <p
            className={`text-[11px] sm:text-xs md:text-sm ${colors.text.secondary} mt-1`}
          >
            {t("blogs.description")}
          </p>
        </div>

        {/* Blog List */}
        {blogs.length === 0 ? (
          <div className={`${colors.background.secondary} rounded-lg p-12 text-center`}>
            <svg className={`mx-auto h-16 w-16 ${colors.text.muted} mb-4`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className={colors.text.secondary}>{t("blog.empty")}</p>
          </div>
        ) : (
          <div className="space-y-2 lg:space-y-4">
            {blogs.map((blog) => (
              <Link
                key={blog.id}
                to={`/blogs/${blog.id}`}
                className={`${colors.background.primary} rounded-lg border ${colors.border} overflow-hidden hover:shadow-lg transition-all block`}
              >
                <div className="flex flex-col md:flex-row">
                  {/* Image */}
                  <div className="md:w-64 h-48 md:h-auto overflow-hidden bg-gradient-to-br from-purple-400 to-pink-600 flex-shrink-0">
                    <DriveImage
                      src={blog.blogImageUrl}
                      alt={blog.title}
                      className="w-full h-full object-cover"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-16 h-16 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      }
                    />
                  </div>

                  {/* Content */}
                  <div className="p-3 md:p-5 lg:p-6 flex-1">
                    <h3 className={`text-base sm:text-lg md:text-2xl font-semibold mb-2 ${colors.text.primary}`}>
                      {blog.title}
                    </h3>
                    {blog.date && (
                      <div className="flex items-center gap-2 mb-2 md:mb-3">
                        <svg className={`w-4 h-4 ${colors.text.muted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <time className={`text-[11px] sm:text-xs md:text-sm ${colors.text.secondary}`}>
                          {new Date(blog.date).toLocaleDateString(activeDateLocale, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </time>
                      </div>
                    )}
                    <p className={`${colors.text.secondary} text-[11px] sm:text-xs md:text-sm line-clamp-3`}>
                      {blog.summary || t("blog.noSummaryAvailable")}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Container>
  );
}
